import { useAppContext } from "@/app/providers/AppContext";
import {
    clearPortalToken,
    readPortalToken,
    writePortalToken,
} from "@/domains/auth/lib/portalToken";
import PortalLoader from "@/shared/components/PortalLoader";
import { generateRequestId, resolveApiUrl } from "@/shared/lib/apiBase";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const portalExchangePromises = new Map<string, Promise<void>>();

async function exchangePortalCodeOnce(code: string): Promise<void> {
  const existingPromise = portalExchangePromises.get(code);
  if (existingPromise) {
    return existingPromise;
  }

  const exchangePromise = (async () => {
    const response = await fetch(resolveApiUrl("/api/auth/portal/exchange"), {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Request-Id": generateRequestId(),
      },
      body: JSON.stringify({ code }),
    });
    if (!response.ok) {
      throw new Error("Authentication exchange failed");
    }

    const data = await response.json();
    if (!data?.access_token) {
      throw new Error("Authentication exchange returned no access token");
    }

    writePortalToken(data.access_token);
  })();

  portalExchangePromises.set(code, exchangePromise);

  try {
    await exchangePromise;
  } catch (error) {
    portalExchangePromises.delete(code);
    throw error;
  }
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const { loadProfile } = useAppContext();

  useEffect(() => {
    let cancelled = false;
    const PROFILE_LOAD_TIMEOUT_MS = 8000;

    const completeAuth = async () => {
      const hashParams = new URLSearchParams(
        window.location.hash.replace(/^#/, ""),
      );
      const searchParams = new URLSearchParams(window.location.search);
      const code = hashParams.get("code") || searchParams.get("code");
      const callbackError = hashParams.get("error") || searchParams.get("error");

      if (callbackError) {
        throw new Error(callbackError);
      }

      const hasPortalToken = Boolean(readPortalToken());
      if (code && !hasPortalToken) {
        await exchangePortalCodeOnce(code);
      }

      if (!readPortalToken()) {
        throw new Error("Missing portal token after authentication callback");
      }

      // Only clear the one-time callback params after the exchange has finished.
      // In React StrictMode the effect mounts twice; keeping the code visible until
      // then lets both mounts share the same in-flight exchange promise.
      if (window.location.hash || window.location.search) {
        window.history.replaceState(null, "", window.location.pathname);
      }

      if (cancelled) return;
      await Promise.race([
        loadProfile(true),
        new Promise<void>((resolve) =>
          window.setTimeout(resolve, PROFILE_LOAD_TIMEOUT_MS),
        ),
      ]);

      if (cancelled) return;

      const pendingPlan = sessionStorage.getItem("pendingPlan");
      if (pendingPlan) {
        sessionStorage.removeItem("pendingPlan");
        navigate(`/portal/account?selectedPlan=${pendingPlan}`, { replace: true });
      } else {
        navigate("/portal", { replace: true });
      }
    };

    completeAuth().catch(() => {
      if (cancelled) return;
      clearPortalToken();
      navigate("/login?error=auth_exchange_failed", { replace: true });
    });

    return () => {
      cancelled = true;
    };
  }, [loadProfile, navigate]);

  return <PortalLoader />;
}
